import { z } from "zod";
import { zPayerTypeSchema } from "@/types/order/order.common";
import { zOrderStatusSchema } from "@/types/order/order.common";
/**
 * DTO schema for v_order_details view row.
 * All fields except id/status are nullable/optional as the view may return nulls.
 */
const zTechnicianSnapshotDto = z.object({
    id: z.string().nullish(),
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
    imgURL: z.string().nullish(),
    rating: z.number().nullish(),
    count_reviews: z.number().nullish(),
    reviews: z.array(z.unknown()).nullish(),
});
const zVehicleSnapshotDto = z.object({
    id: z.string().nullish(),
    name: z.string().nullish(),
    brand: z.string().nullish(),
    model: z.string().nullish(),
    year: z.number().nullish(),
    stateNumber: z.string().nullish(),
});
const zReviewDto = z.object({
    id: z.string().nullish(),
    rating: z.number().nullish(),
    text: z.string().nullish(),
});
const zRoutePointDto = z.object({
    id: z.string().nullish(),
    addressShort: z.string().nullish(),
    addressLocality: z.string().nullish(),
    address: z.string().nullish(),
    orderIndex: z.number().nullish(),
    latitude: z.number().nullish(),
    longitude: z.number().nullish(),
    plannedArrivalTime: z.string().nullish(),
    expectedArrivalTime: z.string().nullish(),
    actualArrivalTime: z.string().nullish(),
    actualDepartureTime: z.string().nullish(),
    updatedAt: z.string().nullish(),
});
export const zOrderDetailsDtoSchema = z.object({
    id: z.string(),
    status: zOrderStatusSchema,
    idClient: z.string().nullish(),
    orderNumber: z.string().nullish(),
    addressId: z.string().nullish(),
    cardId: z.string().nullish(),
    addressShort: z.string().nullish(),
    addressLocality: z.string().nullish(),
    address: z.string().nullish(),
    scheduledDate: z.string().nullish(),
    scheduledDateEnd: z.string().nullish(),
    latitude: z.number().nullish(),
    longitude: z.number().nullish(),
    chargeValue: z.number().int().nullish(),
    planTotalKwt: z.number().nullish(),
    actualTotalKwt: z.number().nullish(),
    currentCharge: z.number().nullish(),
    planCost: z.number().nullish(),
    actualCost: z.number().nullish(),
    isPaid: z.boolean().nullish(),
    currentLatitude: z.number().nullish(),
    currentLongitude: z.number().nullish(),
    beforeChargingPhotos: z.array(z.string()).nullish(),
    inChargingPhotos: z.array(z.string()).nullish(),
    afterChargingPhotos: z.array(z.string()).nullish(),
    startCharge: z.number().int().nullish(),
    currentTotalKwt: z.number().nullish(),
    currentCost: z.number().nullish(),
    actualCharge: z.number().int().nullish(),
    reason_cancellation: z.string().nullish(),
    cancelled_at: z.string().nullish(),
    arrived_at: z.string().nullish(),
    charged_at: z.string().nullish(),
    technician: zTechnicianSnapshotDto.nullish(),
    review: zReviewDto.nullish(),
    route_points: z.array(zRoutePointDto).nullish(),
    photosBeforeCharging: z.array(z.string()).nullish(),
    photosInCharging: z.array(z.string()).nullish(),
    photosAfterCharging: z.array(z.string()).nullish(),
    cardLast4: z.number().nullish(),
    vehicle: zVehicleSnapshotDto.nullish(),
    idConversation: z.string().nullish(),
    tariffPerKwt: z.number().nullish(),
    chargingSeconds: z.number().int().nullish(),
    // FR-007: плательщик и снимок тарифа (контракт v2.0 §8).
    // tariffPerKwt у корп-заказа отдаёт цену снимка, а не живую цену тарифа.
    corpCompanyId: z.string().nullish(),
    corpCompanyName: z.string().nullish(),
    payerType: zPayerTypeSchema.nullish(),
    corpLinkId: z.string().nullish(),
    pricePerKwhSnapshot: z.number().nullish(),
    serviceFeeSnapshot: z.number().nullish(),
});
/**
 * DTO schema for order_statuses table row.
 */
export const zOrderStatusHistoryDtoSchema = z.object({
    id: z.string(),
    idOrder: z.string().nullish(),
    idTechnician: z.string().nullish(),
    previousStatus: zOrderStatusSchema.nullish(),
    newStatus: zOrderStatusSchema.nullish(),
    reason: z.string().nullish(),
    latitude: z.number().nullish(),
    longitude: z.number().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    idCreatedBy: z.string().nullish(),
    idUpdatedBy: z.string().nullish(),
    isDeleted: z.boolean().nullish(),
});
/**
 * DTO schema for charging_sessions table row.
 */
export const zChargingSessionDtoSchema = z.object({
    session_id: z.string(),
    transaction_id: z.string().nullish(),
    connector_id: z.number().int().nullish(),
    route_point_id: z.string().nullish(),
    user_tag_id: z.string().nullish(),
    start_time: z.string().nullish(),
    end_time: z.string().nullish(),
    start_meter_value: z.number().nullish(),
    end_meter_value: z.number().nullish(),
    start_soc: z.number().nullish(),
    end_soc: z.number().nullish(),
    max_power: z.number().nullish(),
    avg_power: z.number().nullish(),
    status: z.string().nullish(),
    stop_reason: z.string().nullish(),
    error_code: z.string().nullish(),
    charging_profile: z.unknown().nullish(),
    meter_values_count: z.number().int().nullish(),
    created_at: z.string().nullish(),
    updated_at: z.string().nullish(),
    charging_station_uuid: z.string().nullish(),
    energy_delivered: z.number().nullish(),
});
